import React, { ComponentType, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { SWRConfig } from 'swr'
import { match } from 'ts-pattern'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { BlendEventType, EventSource, WidgetEventType } from '@frame/lib/api/events/types'
import { Events as TrackingEvent, track } from '@frame/lib/tracking'
import { BasicPartnerEventPayload, FrameConfig } from '@frame/types'
import { version } from '@script/index'
import { appendContainer } from '@script/utils'
import * as Sentry from '@sentry/react'

import { Env } from '../script/types'
import { App } from './components/App'
import { RootContext } from './components/contexts/root-context'
import './index.css'
import { create as createActions } from './lib/actions'
import { create as createApi } from './lib/api'
import * as errors from './lib/errors'
import { events } from './lib/events'
import { emitBlendEvent } from './lib/events/blend'
import { createPartnerEventPayloadCallback } from './lib/hooks/useMonitoring'
import { useStore } from './lib/stores/useStore'

export const PROD = process.env.PRODUCTION

const ErrorFallback = lazy(async () => {
  const { ErrorFallback } = await import('./components/error-fallback')
  return { default: ErrorFallback }
})

Sentry.init({
  environment: PROD ? 'production' : 'development',
  dsn: PROD ? 'https://144bdbe5b48d4538b9f3496ad65fb7e9@o111351.ingest.sentry.io/4504538216071168' : undefined,
  tracesSampleRate: 1.0,
  release: version,
})

// The email flow uses this as a magic value to mean "no publishable key"
const NO_PUBLISHABLE_KEY_INDICATOR = 'publishable_key'
// https://docs.datadoghq.com/integrations/content_security_policy_logs/#session-replay-worker
const workerUrl = new URL('../node_modules/@datadog/browser-worker/bundle/worker.js', import.meta.url).href

function initializeDatadogRum(publishableKey: string) {
  const hasPublishableKey = Boolean(publishableKey && publishableKey !== NO_PUBLISHABLE_KEY_INDICATOR)

  datadogRum.init({
    trackViewsManually: true,
    applicationId: '8005374d-a809-4562-a7ad-78746116ae85',
    clientToken: 'puba22992e799abee8f47a4ab172ecf72cb',
    site: 'datadoghq.com',
    // If we have a publishable key, we're an embedded session that should use the proxy.
    proxy: hasPublishableKey ? 'https://app.truework.com/api/truework-js/monitoring/rum' : undefined,
    workerUrl,
    service: 'truework-direct',
    version,
    useCrossSiteSessionCookie: true,
    ...(PROD
      ? {
          env: 'prod',
          allowedTracingUrls: [
            {
              match: 'https://app.truework.com',
              propagatorTypes: ['datadog', 'tracecontext'],
            },
            {
              match: 'https://app.truework-staging.com',
              propagatorTypes: ['datadog', 'tracecontext'],
            },
            {
              match: 'https://app.truework-sandbox.com',
              propagatorTypes: ['datadog', 'tracecontext'],
            },
          ],
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
        }
      : {
          env: 'dev',
          allowedTracingUrls: [
            {
              match: 'http://truework.test',
              propagatorTypes: ['datadog', 'tracecontext'],
            },
          ],
          sessionSampleRate: 0,
          sessionReplaySampleRate: 0,
        }),
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    enableExperimentalFeatures: ['feature_flags'],
  })

  datadogLogs.init({
    version,
    clientToken: 'puba22992e799abee8f47a4ab172ecf72cb',
    env: PROD ? 'prod' : 'dev',
    forwardConsoleLogs: 'all',
    forwardErrorsToLogs: true,
    proxy: hasPublishableKey ? 'https://app.truework.com/api/truework-js/monitoring/rum' : undefined,
    service: 'truework-direct',
    sessionSampleRate: PROD ? 100 : 0,
    telemetrySampleRate: PROD ? 100 : 0,
  })
}

async function init(config: FrameConfig) {
  initializeDatadogRum(config.publishableKey)

  const referrer = typeof config?.metadata?.referrer === 'string' ? config.metadata.referrer : ''

  useStore.setState({
    config,
    version,
    instanceId: config.instanceId,
    referrer,
  })

  const api = createApi({ sessionToken: config.sessionToken, version, env: config.env })
  const actions = createActions({ api })

  // Similar to useMonitor, but includes defaults and doesn't depend on React.
  function sendWidgetEvent(payload: BasicPartnerEventPayload) {
    const defaultPayload = {
      sessionToken: config.sessionToken,
      instanceId: config.instanceId,
      publishableKey: config.publishableKey,
      version,
      referrer,
    }
    const partnerEventPayload = createPartnerEventPayloadCallback(defaultPayload)({
      created_at: new Date(),
      event_source: EventSource.INTERNAL_WIDGET,
      ...payload,
    })
    api.events.sendPartnerEvent(partnerEventPayload)

    match(payload.event_name)
      .with(WidgetEventType.WIDGET_BOOTED, () => {
        emitBlendEvent({ eventType: BlendEventType.INITIALIZED_MODAL })
      })
      .with(WidgetEventType.WIDGET_INIT_ERROR, () => {
        emitBlendEvent({ eventType: BlendEventType.MODAL_INITIALIZATION_ERROR })
      })
      .with(WidgetEventType.CLOSED_WIDGET, () => {
        emitBlendEvent({ eventType: BlendEventType.CLOSED_MODAL })
      })
      .with(WidgetEventType.WIDGET_ERROR, () => {
        emitBlendEvent({ eventType: BlendEventType.INTERNAL_ERROR })
      })
      .with(WidgetEventType.WIDGET_SUCCESS, () => {
        return
      })
      .exhaustive()
  }

  if (config.env !== Env.Sandbox) {
    actions.hydrateFeatureFlags()
  }

  events.on('open', () => {
    sendWidgetEvent({ event_name: WidgetEventType.WIDGET_BOOTED })
  })

  events.on('complete', () => {
    sendWidgetEvent({ event_name: WidgetEventType.WIDGET_SUCCESS })
  })

  events.on('error', ({ code, message }) => {
    sendWidgetEvent({ event_name: WidgetEventType.WIDGET_ERROR, partner_event: { code, message } })
  })

  events.on('close', () => {
    sendWidgetEvent({ event_name: WidgetEventType.CLOSED_WIDGET })
    events.emit('closed')
  })

  // Start the configuration and render immediately, before we've received a response.
  actions.configureSession(config.publishableKey)

  // TODO: Stop using CSS variables here.
  const style = document.createElement('style')
  style.innerHTML = '      :root {\n' + '        --cobranding-color: none;\n' + '      }'
  document.head.appendChild(style)

  const container = await appendContainer()
  const root = createRoot(container!) // eslint-disable-line @typescript-eslint/no-non-null-assertion

  const renderApp = (Component: ComponentType) => {
    root.render(
      <Sentry.ErrorBoundary
        fallback={({ error, componentStack }) => (
          <ErrorFallback error={error as Error} componentStack={componentStack} />
        )}
        onError={(error, _componentStack) => {
          datadogRum.addError(error)
        }}
      >
        <SWRConfig>
          <RootContext api={api} actions={actions} events={events}>
            <Component />
          </RootContext>
        </SWRConfig>
      </Sentry.ErrorBoundary>
    )
  }

  renderApp(App)

  // HACK: HMR logic for development only!!!
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (process.env.NODE_ENV === 'development' && module?.hot) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    module?.hot?.accept('./components/App', async () => {
      const { App: NextApp } = await import('./components/App')
      renderApp(NextApp)
    })
  }

  // We don't want to send this event until the widget is fully booted.
  sendWidgetEvent({ event_name: WidgetEventType.WIDGET_BOOTED, internal_metadata: config.metadata })
}

// If this is run via Node, events becomes undefined, and so we don't want to try to subcribe to events.
if (events !== undefined) {
  events.on('open', async (data) => {
    try {
      await init(data)
      events.emit('opened')
    } catch (e) {
      errors.create({
        error: e as Error,
        code: errors.codes.Critical,
        displayReason: 'Widget failed to instantiate',
        level: 'fatal',
        setError: useStore.getState().setError,
      })
    }
  })

  events.on('open', () => {
    track(TrackingEvent.Opened)
  })

  events.on('close', () => {
    track(TrackingEvent.Closing)
  })

  events.on('closed', () => {
    track(TrackingEvent.Closed)
  })

  events.on('complete', () => {
    track(TrackingEvent.Completed)
  })

  events.on('error', () => {
    track(TrackingEvent.Error)
  })

  events.on('ping', () => {
    events.emit('pong')
  })
}
